import { Skeleton } from "@/components/ui/skeleton";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";
import { ProductBranchSlice } from "@/store/BranchSlice";
import { PermissionUser } from "@/types/globalTypes";
import axios, { AxiosError } from "axios";
import { Briefcase, Calendar, Mail, PhoneCall } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { IoPersonCircle } from "react-icons/io5";
import { RiShirtLine } from "react-icons/ri";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

type Role = {
    name: string;
    nameAR: string;
    nameEN: string;
    invitable: boolean;
    max: number;
};
export default function EmployeeInfo() {
    const [roles, setRoles] = useState<Role[]>([]);
    const [selectedRole, setSelectedRole] = useState("");
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const [deleteOpen, setDeleteOpen] = useState(false);
    const params = useParams<{ id: string }>();
    const userId = params.id;
    const baseUrl = import.meta.env.VITE_BASE_URL;
    const token = useSelector((state: { auth: { token: string } }) => state.auth.token);
    const [userData, setUserData] = useState<PermissionUser>();
    const [editOpen, setEditOpen] = useState(false);
    useLockBodyScroll(editOpen || deleteOpen);
    const [branches, setBranches] = useState<ProductBranchSlice[]>([])
    const [selectedBranch, setSelectedBranch] = useState<string>();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const toggleEdit = () => {
        setEditOpen(!editOpen);
    };
    const formatUnixTimestamp = (unixTimestamp: number | null): string => {
        if (unixTimestamp === null) {
            return "Invalid date"; // Return a default value
        }

        const date = new Date(unixTimestamp * 1000);

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    };
    function toggleDelete() {
        setDeleteOpen(!deleteOpen);
    }
    async function getBranches() {
        try {
            const options = {
                url: `${baseUrl}/api/dashboard/branches/list`,
                method: "GET",
                headers: {
                    Authorization: token,
                },
            };
            const response = await axios.request(options);

            setBranches(response.data.branches);



        } catch (error) {
            console.error(error);
        }
    }
    async function fetchUserData() {
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/api/dashboard/users/all/${userId}`, {
                headers: {
                    Authorization: token,
                },
            });

            if (response.data.success) {
                setUserData(response.data.user);
            } else {
                console.error("Failed to fetch users data:", response.data.error);
            }

        } catch (error) {
            const axiosError = error as AxiosError<{ error: string }>;
            console.error("Error fetching users data:", axiosError);
            console.error("Details:", axiosError.response?.data?.error || "Unknown error");
        } finally {
            setLoading(false);
        }
    }
    async function getRoles() {
        try {
            const response = await axios.get(`${baseUrl}/api/dashboard/users/roles`, {
                headers: { Authorization: token },
            });
            setRoles(response.data);
        } catch (error) {
            console.error("Error fetching roles:", error);
        }
    }
    async function deleteUser() {
        if (!token) return;
        const toastId = toast.loading(t("orders.loading"));
        try {
            const response = await axios.delete(`${baseUrl}/api/dashboard/users/all/${userId}`, {
                headers: {
                    Authorization: token,
                },
            });
            if (response.data.success) {
                toast.success(t("permissions.userDeleted"));
                toggleDelete();
                navigate(-1)
            } else {
                toast.error(t("permissions.deleteFailed"))
            }
        } catch (error) {
            const axiosError = error as AxiosError<{ error: string }>;
            toast.error(axiosError.response?.data?.error || t("permissions.deleteFailed"));
        } finally {
            toast.dismiss(toastId);
        }
    }
    async function editUserRole() {
        if (!token) return;
        if (selectedRole === "") {
            toast.error(t("permissions.selectRole"));
            return;
        }
        if (selectedRole === "admin" && !selectedBranch) {
            toast.error(t("permissions.selectBranch"));
            return;
        }
        const toastId = toast.loading(t("orders.loading"));

        try {
            const response = await axios.put(
                `${baseUrl}/api/dashboard/users/all/${userId}`,
                { role: selectedRole, ...(selectedRole === "admin" && { branch: selectedBranch }), },
                {
                    headers: {
                        Authorization: token,
                    },
                }
            );

            if (response.data.success) {
                toast.success(t("permissions.editRoleSuccess"));
                toggleEdit();
                fetchUserData();
            } else {
                toast.error(t("permissions.editRoleFailed"));
            }
        } catch (error) {
            const axiosError = error as AxiosError<{ error: string }>;
            toast.error(axiosError.response?.data?.error || t("permissions.editRoleFailed"));
        } finally {
            toast.dismiss(toastId);
        }
    }

    useEffect(() => {
        if (!token) return;
        getRoles();
        fetchUserData();
        getBranches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (loading) {
        return (
            <>
                <Skeleton className="h-[300px] w-[90%] rounded-md mx-auto mt-10 dark:bg-gray-300" />

            </>

        )
    }
    return (
        <>
            {deleteOpen && <>

                <div className="fixed inset-0 z-[99999] bg-black/30 flex items-center justify-center px-4" onClick={toggleDelete}>
                    <div
                        className="bg-white w-full max-w-md rounded-xl p-6 shadow-lg relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="text-lg font-bold text-red-600">{t("permissions.deleteEmployee")}</h2>
                        <p className="mt-2 text-gray-700">
                            {t("permissions.question")} <span className="font-semibold">{userData?.username !== "" ? userData?.username : t("permissions.noName")}</span>, {t("permissions.question2")} <span className="font-semibold">{locale === "en" ? userData?.roleNameEN : userData?.roleNameAR}</span>
                        </p>

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={toggleDelete}
                                className="px-4 py-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
                            >
                                {t("cancel")}
                            </button>
                            <button
                                onClick={deleteUser}
                                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition"
                            >
                                {t("delete")}
                            </button>
                        </div>
                    </div>
                </div>
            </>}
            {editOpen && (
                <div className="fixed inset-0 z-[99999] bg-black/30 flex items-center justify-center px-4" onClick={toggleEdit}>
                    <div
                        className="bg-white w-full max-w-md rounded-xl p-6 shadow-lg relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="text-lg font-bold my-5 text-sidebar">{t("permissions.editRoleTitle")}</h2>
                        <div className="flex flex-col gap-5 text-gray-400">
                            <h3 className="mb-0">{t("permissions.role")}</h3>
                            <select
                                name="role"
                                id="role"
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value)}
                                className="border py-1 rounded-md px-5"

                            >
                                <option className="text-gray-300" value="">{t("permissions.selectFromRole")}</option>
                                {roles
                                    .filter((role: Role) => role.invitable)
                                    .map((role: Role) => (
                                        <option key={role.name} value={role.name} className="text-sidebar">
                                            {locale === "en" ? role.nameEN : role.nameAR}
                                        </option>
                                    ))}
                            </select>
                            {selectedRole === "admin" && branches && (
                                <>
                                    <h3 className="mb-0">{t("permissions.branch")}</h3>
                                    <select
                                        name="branch"
                                        id="branch"
                                        value={selectedBranch}
                                        onChange={(e) => setSelectedBranch(e.target.value)}
                                        className="border py-1 rounded-md px-5"

                                    >
                                        <option className="text-gray-300" value="">{t("permissions.selectBranch")}</option>
                                        {branches.map((branch) => (
                                            <option key={branch._id} value={branch._id} className="text-sidebar">
                                                {locale === "en" ? branch.nameEN : branch.nameAR}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            )
                            }
                        </div>

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={toggleEdit}
                                className="px-4 py-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
                            >
                                {t("cancel")}
                            </button>
                            <button
                                onClick={editUserRole}
                                className="px-4 py-2 rounded-md bg-sidebar text-skyblue hover:brightness-110 transition"
                            >
                                {t("permissions.editRole")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {userData && (<div className="border px-5 py-8">
                <div className="my-5">
                    <h2 className="text-xl font-semibold text-gray-500 flex items-center gap-2">
                        <RiShirtLine size={25} /> {t("permissions.employeeInfo")} {userData.username !== "" ? userData.username : t("permissions.noName")}
                    </h2>
                </div>
                <div className="flex flex-col md:flex-row items-center justify-center gap-10 flex-wrap">
                    {/* Avatar and Name Section */}
                    <div className="flex flex-col gap-1 items-center">
                        <div>
                            {userData.avatar ? (
                                <img src={userData.avatar} alt="User Avatar" className="w-24 h-24 rounded-full object-cover" />
                            ) : (
                                <IoPersonCircle size={130} className="text-gray-400" />
                            )}
                        </div>
                        <span>{userData.username}</span>
                        <span className={`${userData.status === "active" ? "text-green-700 bg-green-300" : "bg-red-600 text-white"} px-3 py-1 text-[10px] rounded-full`}>
                            {userData.status === "active" ? t("permissions.active") : t("permissions.pending")}
                        </span>
                    </div>

                    {/* Info Section */}
                    <div>
                        <div className="space-y-5 text-right text-gray-800 font-medium text-sm leading-relaxed">
                            <div className="flex  flex-wrap items-center gap-2">
                                <div className="flex items-center gap-2">
                                    <Mail className="w-5 h-5 text-gray-600" />
                                    <span>{t("permissions.email")} :</span>
                                </div>
                                <span>{userData.email}</span>
                            </div>

                            <div className="flex  flex-wrap  items-center gap-2">
                                <div className="flex items-center gap-2">
                                    <Briefcase className="w-5 h-5 text-gray-600" />
                                    <span>{t("permissions.role")} :</span>
                                </div>
                                <span> {locale === "ar" ? userData.roleNameAR : userData.roleNameEN}</span>
                            </div>

                            <div className="flex  flex-wrap  items-center gap-2">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-gray-600" />
                                    <span>{t("permissions.joinedDate")} :</span>
                                </div>
                                <span>{formatUnixTimestamp(userData.joinTime)}</span>
                            </div>
                            {userData.role !== "owner" && userData.role !== "superadmin" && (
                                <div className="flex  flex-wrap  items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-5 h-5 text-gray-600" />
                                        <span>{t("permissions.branch")} :</span>
                                    </div>
                                    <span>{locale === "ar" ? userData.branchNameAR : userData.branchNameEN}</span>
                                </div>
                            )}
                            <div className="flex flex-wrap items-center gap-2">
                                {/* Icon + Label */}
                                <div className="flex items-center gap-2">
                                    <PhoneCall className="w-5 h-5" />
                                    <span>{t("permissions.phone")} : </span>
                                </div>

                                {/* Phone Link */}
                                <span

                                    className=" text-sm"
                                >
                                    {userData.phone ? userData.phone : t("permissions.noPhone")}
                                </span>
                            </div>

                        </div>
                    </div>
                </div>

                {userData.role !== "owner" && (
                    <div className="flex items-center w-fit ms-auto gap-3 mt-5">
                        <button
                            onClick={toggleDelete}
                            className="bg-red-500 text-white px-5 py-2 rounded-full block ms-auto mt-5"
                        >
                            {t("delete")}
                        </button>
                        <button
                            onClick={toggleEdit}
                            className="bg-sidebar text-skyblue px-5 py-2 rounded-full block ms-auto mt-5"
                        >
                            {t("permissions.edit")}
                        </button>
                    </div>
                )}

            </div>)}
        </>
    )
}
